import { useRafFn, tryOnScopeDispose } from '@vueuse/core'

// 类型定义保持不变
export interface TaskOptions {
  key: symbol | string
  interval: number
  callback: () => Promise<void> | void
  immediate?: boolean
}

interface TaskItem extends TaskOptions {
  lastTime: number
  isExecuting: boolean
}

// 全局任务队列（闭包内的私有变量）
const tasks = new Map<symbol | string, TaskItem>()

/**
 * 核心引擎：使用 useRafFn 替代手写的 loop
 * { immediate: false } 表示初始化时不立即启动，等有任务了再启动
 */
const { pause, resume, isActive } = useRafFn(
  ({ timestamp }) => {
    // 如果队列为空，自动休眠（双重保险）
    if (tasks.size === 0) {
      pause()
      return
    }

    tasks.forEach(async task => {
      // 1. 异步锁：如果正在执行，直接跳过
      if (task.isExecuting) return

      // 2. 时间计算
      const diff = timestamp - task.lastTime

      if (diff >= task.interval) {
        // 重置时间 (使用当前帧时间戳)
        task.lastTime = timestamp
        task.isExecuting = true

        try {
          await task.callback()
        } catch (error) {
          console.error(`[RafTimer] Task ${String(task.key)} Error:`, error)
        } finally {
          task.isExecuting = false
        }
      }
    })
  },
  { immediate: false },
)

/**
 * 对外暴露的操作对象
 */
export const rafTimer = {
  /**
   * 添加任务
   * @param options 配置项
   * @param autoRemove 是否在当前组件卸载时自动移除任务 (默认为 true)
   */
  addTask(options: TaskOptions, autoRemove = true) {
    const { key, immediate = false } = options

    const task: TaskItem = {
      ...options,
      lastTime: performance.now(),
      isExecuting: false,
    }

    tasks.set(key, task)

    // 只要有任务，确保引擎是运行的
    if (!isActive.value) {
      resume()
    }

    // 立即执行一次
    if (immediate) {
      // 这里的立即执行可以不走锁，或者简单封装一下
      Promise.resolve(task.callback()).catch(console.error)
    }

    // 【优化点】利用 VueUse 的 tryOnScopeDispose
    // 如果这个函数是在组件 setup 中调用的，它会自动绑定销毁逻辑
    // 这样用户在组件里甚至不需要写 onUnmounted
    if (autoRemove) {
      tryOnScopeDispose(() => {
        rafTimer.removeTask(key)
      })
    }
  },

  removeTask(key: symbol | string) {
    if (tasks.has(key)) {
      tasks.delete(key)
    }
    // 队列空了，暂停引擎，节省资源
    if (tasks.size === 0) {
      pause()
    }
  },

  // 暴露当前状态（可选，用于调试）
  get isRunning() {
    return isActive.value
  },
}
